/**
 * Thin client for the public account-deletion endpoints on the TableTalk
 * Symfony backend. The form at /account/delete is anonymous (no JWT) — the
 * magic-link token sent to the user's email is the only auth surface.
 *
 * See I:/deli/tabletalk/src/Controller/Public/PublicAccountDeletionController.php
 * for the canonical request/response shapes.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? "https://app.tabletalk.ro";

async function post(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  // 204 (success-no-body) → null. Other 2xx with body → parsed JSON.
  if (res.status === 204) return { ok: true, status: 204, data: null };
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON body */ }
  return { ok: res.ok, status: res.status, data };
}

async function get(path) {
  const res = await fetch(`${API_BASE}${path}`, { method: "GET" });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON body */ }
  return { ok: res.ok, status: res.status, data };
}

/**
 * Submit the deletion request. Always succeeds (204) when the input is
 * valid — the backend does not leak whether the email exists. A 400 means
 * the email was malformed or the captcha failed; 429 means rate-limited;
 * 500 means the confirmation email couldn't be sent.
 */
export function requestDeletion({ email, reason, turnstileToken, locale }) {
  return post("/api/public/account/delete/request", {
    email,
    reason: reason ?? null,
    turnstileToken,
    locale
  });
}

/**
 * Inspect a magic-link token without consuming it. Used by the confirm
 * page to render a summary (email, displayName, last-admin blocker)
 * before the user clicks the destructive button.
 */
export function validateDeletionToken(token) {
  const params = new URLSearchParams({ token });
  return get(`/api/public/account/delete/validate?${params}`);
}

/**
 * Consume the token and soft-delete the account. Server returns 200 on
 * success, 409 (last_admin) if the user is the only admin of an
 * organization, or 403 if the token is invalid/used/expired.
 */
export function confirmDeletion({ token, reason }) {
  return post("/api/public/account/delete/confirm", { token, reason: reason ?? null });
}
